import { audio } from '../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../../ui/TouchTarget.js';
import { NUMBER_KINGDOM_ASSETS } from '../../NumberKingdomAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  completeNumberKingdomDistrictProgress,
  getNumberKingdomState,
  saveNumberKingdomDistrictProgress,
  visitNumberKingdomDistrict,
} from '../../NumberKingdomState.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../components/NumberKingdomDistrictLayout.js';
import { createNumberKingdomHomeButton, type NumberKingdomHomeButton } from '../../components/NumberKingdomHomeButton.js';
import { SUBTRACTION_KEEP_ASSETS } from '../subtraction-keep/SubtractionKeepAssets.js';
import {
  CLOCKTOWER_PLAZA_ASSETS,
  CLOCKTOWER_PLAZA_ASSET_LIST,
  canEnterClocktowerPlaza,
  getMissingClocktowerPlazaAssets,
} from './ClocktowerPlazaAssets.js';
import {
  CLOCKTOWER_PLAZA_CHALLENGES,
  formatClockTime,
  type ClocktowerPlazaChallengeConfig,
} from './ClocktowerPlazaConfig.js';
import {
  ClocktowerPlazaState,
  type ClocktowerPlazaSerializedState,
} from './ClocktowerPlazaState.js';
import { canEnterCoinmakersVault } from '../coinmakers-vault/CoinmakersVaultAssets.js';

export const CLOCKTOWER_PLAZA_SCENE_KEY = 'ClocktowerPlaza';
const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

interface ClocktowerPlazaProgress {
  readonly version: 1;
  readonly challengeIndex: number;
  readonly challengeId: string;
  readonly challengeState: ClocktowerPlazaSerializedState;
  readonly completedChallengeIds: readonly string[];
  readonly restored: boolean;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

export class ClocktowerPlazaScene extends Phaser.Scene {
  static readonly requiredImageKeys = CLOCKTOWER_PLAZA_ASSET_LIST.map(asset => asset.key);

  private challengeIndex = 0;
  private completedChallengeIds = new Set<string>();
  private challenge!: ClocktowerPlazaState;
  private dormantBackground!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private missionPanel!: Phaser.GameObjects.Image;
  private clockFace!: Phaser.GameObjects.Image;
  private hourHand!: Phaser.GameObjects.Image;
  private minuteHand!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private missionText!: Phaser.GameObjects.Text;
  private selectedTimeText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private nextText!: Phaser.GameObjects.Text;
  private numerals: Phaser.GameObjects.Text[] = [];
  private buttons: ArtButton[] = [];
  private restored = false;
  private leaving = false;
  private suspended = false;
  private reducedMotion = false;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: CLOCKTOWER_PLAZA_SCENE_KEY }); }

  create() {
    this.numerals = [];
    this.buttons = [];
    this.challengeIndex = 0;
    this.completedChallengeIds = new Set<string>();
    this.leaving = false;
    this.suspended = false;

    const world = getNumberKingdomState();
    const missing = getMissingClocktowerPlazaAssets(this.textures);
    if (!world.districtUnlocks.includes(NUMBER_KINGDOM_DISTRICTS.clockTower)
      || !canEnterClocktowerPlaza(this.textures)) {
      if (missing.length > 0) {
        console.error('[ClocktowerPlaza] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('MeasurementMarket');
      return;
    }

    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.restored = world.districtCompletion[NUMBER_KINGDOM_DISTRICTS.clockTower] === true;
    this.restoreProgress(world.districtProgress[NUMBER_KINGDOM_DISTRICTS.clockTower]);
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.clockTower);
    SaveManager.setLastScene(CLOCKTOWER_PLAZA_SCENE_KEY);
    SaveManager.save();
    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.cameras.main.fadeIn(this.reducedMotion ? 140 : 420, 255, 225, 170);
  }

  private restoreProgress(value: unknown) {
    const progress = value as Partial<ClocktowerPlazaProgress> | undefined;
    if (progress?.version === 1) {
      this.challengeIndex = Phaser.Math.Clamp(
        Number.isInteger(progress.challengeIndex) ? Number(progress.challengeIndex) : 0,
        0,
        CLOCKTOWER_PLAZA_CHALLENGES.length - 1,
      );
      this.completedChallengeIds = new Set(
        (progress.completedChallengeIds ?? []).filter(id =>
          CLOCKTOWER_PLAZA_CHALLENGES.some(config => config.id === id)),
      );
      this.restored ||= progress.restored === true;
      const config = CLOCKTOWER_PLAZA_CHALLENGES[this.challengeIndex];
      const saved = progress.challengeId === config.id ? progress.challengeState : undefined;
      this.challenge = new ClocktowerPlazaState(config, saved);
      return;
    }
    this.challenge = new ClocktowerPlazaState(CLOCKTOWER_PLAZA_CHALLENGES[0]);
  }

  private buildEnvironment() {
    this.dormantBackground = this.add.image(640, 360, CLOCKTOWER_PLAZA_ASSETS.dormant.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(640, 360, CLOCKTOWER_PLAZA_ASSETS.restored.key)
      .setDisplaySize(1280, 720).setDepth(1);
    this.missionPanel = this.add.image(640, 94, SUBTRACTION_KEEP_ASSETS.missionPanel.key).setDepth(40);
    this.clockFace = this.add.image(640, 385, CLOCKTOWER_PLAZA_ASSETS.face.key).setDepth(55);
    this.hourHand = this.add.image(640, 385, CLOCKTOWER_PLAZA_ASSETS.hourHand.key)
      .setOrigin(0.5, 0.89).setDepth(75).setInteractive({ useHandCursor: true });
    this.minuteHand = this.add.image(640, 385, CLOCKTOWER_PLAZA_ASSETS.minuteHand.key)
      .setOrigin(0.5, 0.89).setDepth(76).setInteractive({ useHandCursor: true });
    this.hourHand.on('pointerup', () => this.advanceHour());
    this.minuteHand.on('pointerup', () => this.advanceMinute());
    this.cosmo = this.add.image(105, 640, NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key).setDepth(105);
  }

  private buildInterface() {
    this.titleText = this.makeText('CLOCKTOWER PLAZA', 28, '#fff2ad', 'bold')
      .setStroke('#2c1746', 7).setDepth(110);
    this.missionText = this.makeText('', 16, '#ffffff', 'bold')
      .setStroke('#233a69', 5).setWordWrapWidth(670).setDepth(110);
    this.selectedTimeText = this.makeText('', 24, '#fff2ad', 'bold')
      .setStroke('#26375c', 7).setDepth(110);
    this.feedbackText = this.makeText('', 14, '#ffffff', 'bold')
      .setStroke('#26375c', 5).setWordWrapWidth(720).setDepth(110);
    this.controlsText = this.makeText(
      'Tap the short hand for +1 hour • long hand for +5 minutes • Enter check/next • H hint • R reset',
      11,
      '#fff8db',
      'bold',
    ).setStroke('#26375c', 5).setDepth(110);
    this.backText = this.makeText('←  MEASUREMENT MARKET', 14, '#ffffff', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);
    this.nextText = this.makeText("COINMAKER'S VAULT • NEXT DISTRICT", 14, '#fff2ad', 'bold')
      .setStroke('#26375c', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.backText, this.nextText].forEach(target => ensureMinimumTextTouchTarget(target));
    this.backText.on('pointerup', this.returnToMarket, this);
    this.nextText.on('pointerup', this.travelToCoinmakersVault, this);
    for (let value = 1; value <= 12; value += 1) {
      this.numerals.push(this.makeText(String(value), 16, '#ffffff', 'bold')
        .setStroke('#26375c', 5).setDepth(70));
    }
    this.buttons = [
      this.makeButton('HOUR +1', () => this.advanceHour()),
      this.makeButton('MINUTE +5', () => this.advanceMinute()),
      this.makeButton('CHECK', () => this.primaryAction()),
      this.makeButton('HINT', () => this.hint()),
      this.makeButton('RESET', () => this.resetChallenge()),
    ];
  }

  private makeText(value: string, size: number, color: string, style: string) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_UI,
      fontSize: `${size}px`,
      fontStyle: style,
      color,
      align: 'center',
    }).setOrigin(0.5);
  }

  private makeButton(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, SUBTRACTION_KEEP_ASSETS.actionButton.key)
      .setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 12, '#ffffff', 'bold').setStroke('#26375c', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label: text };
  }

  private advanceHour(delta = 1) {
    if (!this.canAct() || !this.challenge.advanceHour(delta)) return;
    this.feedbackText.setText('The short hand clicks to the next hour.');
    audio.click();
    this.capture();
  }

  private advanceMinute(delta = 5) {
    if (!this.canAct() || !this.challenge.advanceMinute(delta)) return;
    this.feedbackText.setText('The long hand advances one five-minute interval.');
    audio.click();
    this.capture();
  }

  private primaryAction() {
    if (this.restored) return;
    if (this.challenge.snapshot.complete) return this.startNextChallenge();
    if (this.challenge.check() === 'incorrect') {
      this.feedbackText.setText('The tower is not synchronized yet. Revise either hand and check again.');
      audio.wrong();
      return this.capture();
    }
    const config = this.currentConfig;
    this.completedChallengeIds.add(config.id);
    SaveManager.recordAnswer(config.skillId, true);
    this.feedbackText.setText(`${formatClockTime(config.targetHour, config.targetMinute)} is synchronized! The plaza gears turn.`);
    audio.correct();
    if (this.completedChallengeIds.size === CLOCKTOWER_PLAZA_CHALLENGES.length) this.completeDistrict();
    else {
      this.buttons[2].label.setText('NEXT TIME');
      this.playMagicBurst(640, 380, 320);
    }
    this.capture();
  }

  private startNextChallenge() {
    const nextIndex = CLOCKTOWER_PLAZA_CHALLENGES.findIndex(
      config => !this.completedChallengeIds.has(config.id),
    );
    if (nextIndex < 0) return this.completeDistrict();
    this.challengeIndex = nextIndex;
    this.challenge = new ClocktowerPlazaState(this.currentConfig);
    this.feedbackText.setText('A new bell schedule slides into the clockwork console.');
    this.buttons[2].label.setText('CHECK');
    this.capture();
  }

  private completeDistrict() {
    this.restored = true;
    this.completedChallengeIds = new Set(CLOCKTOWER_PLAZA_CHALLENGES.map(config => config.id));
    completeNumberKingdomDistrictProgress(
      NUMBER_KINGDOM_DISTRICTS.clockTower,
      this.progressSnapshot(),
      NUMBER_KINGDOM_DISTRICTS.royalMarket,
    );
    this.feedbackText.setText("Every bell is synchronized. The Coinmaker's Vault route is restored!");
    this.playMagicBurst(640, 360, 560);
    audio.restoration();
  }

  private hint() {
    if (!this.canAct()) return;
    this.feedbackText.setText(this.challenge.requestHint() ?? 'Read the hour and count minute marks by fives.');
    audio.click();
    this.capture();
  }

  private resetChallenge() {
    if (!this.canAct()) return;
    this.challenge = new ClocktowerPlazaState(this.currentConfig);
    this.feedbackText.setText('This clock is reset. Previously synchronized schedules remain safe.');
    audio.click();
    this.capture();
  }

  private capture() {
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.clockTower, this.progressSnapshot());
    this.refresh();
  }

  private progressSnapshot(): ClocktowerPlazaProgress {
    return {
      version: 1,
      challengeIndex: this.challengeIndex,
      challengeId: this.currentConfig.id,
      challengeState: this.challenge.serialize(),
      completedChallengeIds: [...this.completedChallengeIds],
      restored: this.restored,
    };
  }

  private refresh() {
    const snapshot = this.challenge.snapshot;
    this.restoredBackground.setAlpha(this.restored ? 1 : 0);
    this.dormantBackground.setAlpha(this.restored ? 0 : 1);
    this.missionText.setText(this.restored
      ? "Clocktower Plaza is synchronized. The open road leads to Coinmaker's Vault."
      : `Schedule ${this.challengeIndex + 1} of ${CLOCKTOWER_PLAZA_CHALLENGES.length}: ${this.currentConfig.prompt}`);
    this.selectedTimeText.setText(this.restored
      ? 'CLOCKTOWER RESTORED'
      : `YOUR CLOCK  ${formatClockTime(snapshot.selectedHour, snapshot.selectedMinute)}`);
    this.clockFace.setVisible(!this.restored);
    this.hourHand.setVisible(!this.restored).setRotation(Phaser.Math.DegToRad(snapshot.hourAngle));
    this.minuteHand.setVisible(!this.restored).setRotation(Phaser.Math.DegToRad(snapshot.minuteAngle));
    this.numerals.forEach(text => text.setVisible(!this.restored));
    this.buttons.forEach(button => {
      button.image.setVisible(!this.restored);
      button.label.setVisible(!this.restored);
    });
    this.controlsText.setVisible(!this.restored);
    this.nextText.setVisible(this.restored);
    this.buttons[2].label.setText(snapshot.complete ? 'NEXT TIME' : 'CHECK');
    if (!this.feedbackText.text) {
      this.feedbackText.setText('Move the hands, check the time, and revise freely.');
    }
    this.layout();
  }

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    const portrait = safe.width < 700;
    const panelWidth = portrait ? safe.width - 30 : Math.min(compact ? 650 : 740, safe.width - 230);
    this.missionPanel.setPosition(safe.centerX, safe.y + (portrait ? 126 : compact ? 82 : 92))
      .setDisplaySize(panelWidth, panelWidth * (360 / 900));
    this.titleText.setPosition(safe.centerX, safe.y + (portrait ? 58 : 25))
      .setFontSize(portrait ? 21 : compact ? 22 : 28);
    this.missionText.setPosition(safe.centerX, safe.y + (portrait ? 132 : compact ? 87 : 98))
      .setFontSize(portrait ? 12 : compact ? 13 : 16).setWordWrapWidth(panelWidth * 0.8);
    this.backText.setPosition(safe.x + (portrait ? 238 : compact ? 250 : 282), safe.y + 30)
      .setFontSize(portrait ? 9 : compact ? 11 : 14);
    this.homeButton.layout(safe, compact || portrait, 'left');
    this.selectedTimeText.setPosition(safe.centerX, safe.y + (portrait ? 222 : compact ? 170 : 185))
      .setFontSize(portrait ? 19 : compact ? 21 : 24);

    const faceSize = portrait ? Math.min(390, safe.width - 80) : compact ? 400 : 440;
    const centerY = safe.y + (portrait ? 405 : compact ? 385 : 400);
    this.clockFace.setPosition(safe.centerX, centerY).setDisplaySize(faceSize, faceSize);
    this.hourHand.setPosition(safe.centerX, centerY).setDisplaySize(faceSize * 0.09, faceSize * 0.31);
    this.minuteHand.setPosition(safe.centerX, centerY).setDisplaySize(faceSize * 0.075, faceSize * 0.39);
    const numeralRadius = faceSize * 0.365;
    this.numerals.forEach((text, index) => {
      const value = index + 1;
      const angle = value / 12 * Math.PI * 2 - Math.PI / 2;
      text.setPosition(
        safe.centerX + Math.cos(angle) * numeralRadius,
        centerY + Math.sin(angle) * numeralRadius,
      ).setFontSize(portrait ? 12 : compact ? 14 : 16);
    });

    const buttonWidth = portrait ? 105 : compact ? 118 : 128;
    const gap = portrait ? Math.min(110, (safe.width - buttonWidth) / 4) : compact ? 130 : 140;
    const buttonY = safe.bottom - (compact ? 70 : 76);
    this.buttons.forEach((button, index) => {
      const x = safe.centerX + (index - 2) * gap;
      button.image.setPosition(x, buttonY).setDisplaySize(buttonWidth, portrait ? 52 : compact ? 58 : 64);
      button.label.setPosition(x, buttonY).setFontSize(portrait ? 8 : compact ? 10 : 11);
    });
    this.feedbackText.setPosition(safe.centerX, safe.bottom - (compact ? 116 : 130))
      .setFontSize(portrait ? 11 : compact ? 12 : 14)
      .setWordWrapWidth(portrait ? safe.width - 45 : Math.min(720, safe.width - 230));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 18)
      .setFontSize(portrait ? 8 : compact ? 9 : 11).setWordWrapWidth(portrait ? safe.width - 30 : safe.width);
    this.nextText.setPosition(safe.centerX, safe.bottom - 42).setFontSize(portrait ? 11 : 14);
    this.cosmo.setPosition(safe.x + (compact ? 56 : 72), safe.bottom - (compact ? 48 : 58))
      .setDisplaySize(compact ? 84 : 104, compact ? 76 : 93);
  }

  private playMagicBurst(x: number, y: number, size: number) {
    const burst = this.add.image(x, y, SUBTRACTION_KEEP_ASSETS.magicBurst.key)
      .setDisplaySize(size, size).setDepth(95).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.68 : 0);
    if (this.reducedMotion) this.time.delayedCall(650, () => burst.destroy());
    else this.tweens.add({
      targets: burst,
      alpha: { from: 0, to: 0.9 },
      scaleX: 1.12,
      scaleY: 1.12,
      duration: 500,
      yoyo: true,
      hold: 120,
      onComplete: () => burst.destroy(),
    });
  }

  private registerInput() {
    this.input.keyboard?.on('keydown', this.handleKeyDown, this);
  }

  private handleKeyDown(event: KeyboardEvent) {
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToMarket();
    if (key === 'm') return this.returnHome();
    if (key === 'k' && this.restored) return this.travelToCoinmakersVault();
    if (key === 'enter' || key === ' ') return this.primaryAction();
    if (key === 'h') return this.hint();
    if (key === 'r') return this.resetChallenge();
    if (key === 'arrowup' || key === 'a') return this.advanceHour();
    if (key === 'arrowright' || key === 'd') this.advanceMinute();
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.suspended = true; },
      onResume: () => { this.suspended = false; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private get currentConfig(): ClocktowerPlazaChallengeConfig {
    return CLOCKTOWER_PLAZA_CHALLENGES[this.challengeIndex];
  }

  private canAct() {
    return !this.leaving && !this.suspended && !this.restored && !this.challenge.snapshot.complete;
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private readonly returnToMarket = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.clockTower, this.progressSnapshot());
    this.scene.start('MeasurementMarket');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.clockTower, this.progressSnapshot());
    SaveManager.setLastScene(CLOCKTOWER_PLAZA_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly travelToCoinmakersVault = () => {
    if (!this.restored || this.leaving) return;
    if (!canEnterCoinmakersVault(this.textures)) {
      this.feedbackText.setText("The route is open. Coinmaker's Vault is awaiting its runtime QA gate.");
      return audio.click();
    }
    this.leaving = true;
    saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.clockTower, this.progressSnapshot());
    this.scene.start('CoinmakersVault');
  };

  private shutdown() {
    if (this.challenge) {
      saveNumberKingdomDistrictProgress(NUMBER_KINGDOM_DISTRICTS.clockTower, this.progressSnapshot());
    }
    this.unsubscribeLifecycle?.();
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.tweens.killAll();
  }
}
